import React, { useMemo, useState } from 'react';
import * as S from './RoomChargesPanel.styled';
import Loader from 'pages/components/Loader/Loader';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { bumpRightBarDismiss, selectCurrency } from 'pages/redux';
import { RoomCharge } from 'types';
import { selectRooms, updateRoomCharges } from 'pages/Hospitality/redux';
import { useLocalRoomUpdate } from 'pages/Hospitality/hooks';
import { useSocketEmit } from 'pages/Navigation/hooks';
import { SetModalContext } from 'pages/components/RightBar/RightBar';
import { toast } from 'utils/toast';
import RoomChargeConfirm from './RoomChargeConfirm';
import ReceiptLong from '@mui/icons-material/ReceiptLong';
import Payments from '@mui/icons-material/Payments';
import { selectLiveView, selectPermission } from 'redux/global';
import {
  canAdjustRoomCharge,
  canDeleteRoomCharge,
  canHandleRoomCharge,
} from 'pages/Settings/redux';
import { mockDismissRoomChargeForSession } from 'pages/Navigation/components/CheckoutRoomChargeAlert';

type RoomChargeAction = 'adjust' | 'payAll' | 'deleteAll';

const RoomChargesPanel = ({
  selectedRoomId,
  bookingId,
  mockSessionSettle,
}: {
  selectedRoomId: string,
  bookingId?: string,
  /** From checkout alert — pay only session-mocks; charge returns next sign-in. */
  mockSessionSettle?: boolean,
}) => {
  const { setModal, setCenterModal } = React.useContext(SetModalContext);
  const onExit = () => setModal(null);
  const dispatch = useAppDispatch();
  const currency = useAppSelector(selectCurrency);
  const permission = useAppSelector(selectPermission);
  const liveView = useAppSelector(selectLiveView);
  const rooms = useAppSelector(selectRooms);
  const { editLocalRoom } = useLocalRoomUpdate();
  const socketEmit = useSocketEmit();
  const [loading, setLoading] = useState(false);
  const [adjustAmounts, setAdjustAmounts] = useState<Record<string, string>>({});
  const [adjustErrors, setAdjustErrors] = useState<Record<string, string>>({});
  // Front Desk II+: adjust amounts / delete charges (Front Desk I can still add & pay)
  const canAdjust = canAdjustRoomCharge(permission);
  const canDelete = canDeleteRoomCharge(permission);

  const room = (rooms || []).find((r) => r.id.toString() === selectedRoomId?.toString());
  const charges = useMemo(() => {
    const all = room?.roomCharge || [];
    if (!bookingId) return all;
    return all.filter((c) => c.bookingId === bookingId);
  }, [room, bookingId]);

  const total = charges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
  const bookerName = charges[0]?.bookerName || '';
  const chargeCountLabel = charges.length === 1
    ? '1 outstanding charge'
    : `${charges.length} outstanding charges`;

  if (!canHandleRoomCharge(permission)) {
    onExit();
    return null;
  }

  if (!room) {
    onExit();
    return null;
  }

  if (!charges.length) {
    onExit();
    return null;
  }

  const applyLocalUpdate = (roomCharge: RoomCharge[], updatedAsOf: string) => {
    editLocalRoom([{ id: room.id, roomCharge, updatedAsOf } as any]);
    socketEmit('edit_room', { rooms: [{ id: room.id, roomCharge, updatedAsOf }] });
  };

  const runAction = async (
    action: RoomChargeAction,
    chargeId?: string,
    amount?: string,
  ) => {
    if (action === 'adjust' && !canAdjust) {
      toast('Front Desk II or above required to adjust room charges', { type: 'error' });
      throw new Error('Insufficient permission');
    }
    if (action === 'deleteAll' && !canDelete) {
      toast('Front Desk II or above required to delete room charges', { type: 'error' });
      throw new Error('Insufficient permission');
    }
    // Alert path (always) + live demo: pay only session-hides — charge returns next sign-in
    if ((mockSessionSettle || liveView) && action === 'payAll') {
      const settleKey = bookingId || charges[0]?.bookingId || charges[0]?.id;
      if (settleKey) mockDismissRoomChargeForSession(String(settleKey));
      dispatch(bumpRightBarDismiss());
      toast('Payment recorded', { type: 'success' });
      onExit();
      return;
    }
    // Live demo: non-pay mutations stay blocked (shared showcase data)
    if (liveView) {
      toast(
        'This is a live LodgeFirst demo — Summit Lodge is view-only. Sign up free to manage your own property.',
        { type: 'error' },
      );
      throw new Error('Live demo is view-only');
    }

    setLoading(true);
    const res = await dispatch(updateRoomCharges({
      roomId: room.id,
      action,
      chargeId,
      bookingId,
      amount,
    }));
    setLoading(false);
    if (res.status !== 'success') {
      toast(res.data, { type: 'error' });
      throw new Error(typeof res.data === 'string' ? res.data : 'Action failed');
    }
    const { roomCharge, updatedAsOf } = res.data || {};
    applyLocalUpdate(roomCharge || [], updatedAsOf);
    if (action === 'payAll') {
      toast('Payment recorded', { type: 'success' });
    } else if (action === 'deleteAll') {
      toast('Room charge deleted', { type: 'success' });
    } else {
      toast('Charge adjusted', { type: 'success' });
    }
    const remaining = (roomCharge || []).filter((c: RoomCharge) => (
      bookingId ? c.bookingId === bookingId : true
    ));
    if (!remaining.length) onExit();
  };

  /** Confirm before pay all / delete all / adjust — same centered overlay as keys. */
  const openConfirm = ({
    title,
    message,
    detail,
    confirmLabel,
    isDanger,
    isCash,
    action,
    chargeId,
    amount,
  }: {
    title: string,
    message: string,
    detail?: string,
    confirmLabel: string,
    isDanger?: boolean,
    isCash?: boolean,
    action: RoomChargeAction,
    chargeId?: string,
    amount?: string,
  }) => {
    setCenterModal({
      component: (
        <RoomChargeConfirm
          title={title}
          message={message}
          detail={detail}
          confirmLabel={confirmLabel}
          isDanger={isDanger}
          isCash={isCash}
          onConfirm={() => runAction(action, chargeId, amount)}
        />
      ),
      exitOnBgClick: () => setCenterModal(null),
    });
  };

  const parseAmountInput = (value: string, chargeId: string) => {
    const formatted = value.replaceAll(',', '').replaceAll(currency || '', '');
    if (formatted.includes('-') || /\D+/.test(formatted)) {
      setAdjustErrors((prev) => ({ ...prev, [chargeId]: 'Not a valid entry' }));
      return;
    }
    setAdjustErrors((prev) => ({ ...prev, [chargeId]: '' }));
    setAdjustAmounts((prev) => ({ ...prev, [chargeId]: formatted }));
  };

  const formatMoney = (value: number | string) => (
    `${currency}${Number(value || 0).toLocaleString()}`
  );

  return (
    <S.Container>
      {loading && <Loader />}
      <S.Header>
        <S.HeaderIcon>
          <ReceiptLong />
        </S.HeaderIcon>
        <S.HeaderText>
          <S.MainLabel>Room Charges</S.MainLabel>
          <S.HeaderSub>{chargeCountLabel}</S.HeaderSub>
        </S.HeaderText>
      </S.Header>

      <S.Summary>
        <S.SummaryLeft>
          <S.RoomName>{room.name}</S.RoomName>
          {bookerName ? <S.BookerLine>{bookerName}</S.BookerLine> : null}
        </S.SummaryLeft>
        <S.SummaryRight>
          <S.TotalLabel>Outstanding</S.TotalLabel>
          <S.TotalAmount>{formatMoney(total)}</S.TotalAmount>
        </S.SummaryRight>
      </S.Summary>

      <S.ChargesColumn>
        <S.ChargesList>
          {charges.map((charge) => {
            const draft = adjustAmounts[charge.id] ?? charge.amount;
            const itemLabel = charge.inventory_item_name
              ? `${charge.inventory_item_name}${
                charge.units != null && charge.units !== ''
                  ? ` ×${charge.units}`
                  : ''
              }`
              : '';
            const chargeLabel = itemLabel || charge.description || 'Room charge';
            const dirty = draft !== charge.amount;
            return (
              <S.ChargeCard key={charge.id}>
                <S.ChargeTop>
                  <S.ChargeInfo>
                    <S.ChargeDescription>{chargeLabel}</S.ChargeDescription>
                    <S.ChargeMeta>
                      {charge.branch ? (
                        <S.ChargeBranch>{charge.branch}</S.ChargeBranch>
                      ) : null}
                      {charge.branch && dirty ? <span>·</span> : null}
                      {dirty ? (
                        <span>
                          Was
                          {' '}
                          {formatMoney(charge.amount)}
                        </span>
                      ) : null}
                    </S.ChargeMeta>
                  </S.ChargeInfo>
                  {!dirty ? (
                    <S.ChargeAmount>{formatMoney(charge.amount)}</S.ChargeAmount>
                  ) : null}
                </S.ChargeTop>

                {canAdjust ? (
                  <S.AdjustBlock>
                    <S.AdjustLabel>Adjust amount</S.AdjustLabel>
                    <S.AdjustRow>
                      <S.AmountInput
                        isError={Boolean(adjustErrors[charge.id])}
                        value={
                          draft
                            ? formatMoney(draft)
                            : ''
                        }
                        placeholder={`${currency}0`}
                        onChange={(e) => parseAmountInput(e.target.value, charge.id)}
                      />
                      <S.SecondaryBtn
                        disabled={loading || !dirty || Boolean(adjustErrors[charge.id])}
                        onClick={() => openConfirm({
                          title: 'Adjust room charge',
                          message: 'Confirm adjusting this room charge amount.',
                          detail: `${formatMoney(charge.amount)} → ${formatMoney(draft)}`,
                          confirmLabel: 'Adjust',
                          action: 'adjust',
                          chargeId: charge.id,
                          amount: draft,
                        })}
                      >
                        Adjust
                      </S.SecondaryBtn>
                    </S.AdjustRow>
                    {adjustErrors[charge.id] ? (
                      <S.ErrorText>{adjustErrors[charge.id]}</S.ErrorText>
                    ) : null}
                  </S.AdjustBlock>
                ) : null}
              </S.ChargeCard>
            );
          })}
        </S.ChargesList>

        <S.ActionsRow>
          <S.PayAllBtn
            disabled={loading}
            onClick={() => openConfirm({
              title: 'Pay all charges',
              message: 'Confirm payment received for all outstanding room charges shown here.',
              detail: `${bookerName} · ${formatMoney(total)}`,
              confirmLabel: 'Confirm',
              isCash: true,
              action: 'payAll',
            })}
          >
            <Payments sx={{ fontSize: 18 }} />
            Pay all
            {' '}
            {formatMoney(total)}
          </S.PayAllBtn>
          {canDelete ? (
            <S.DeleteAllBtn
              disabled={loading}
              onClick={() => openConfirm({
                title: 'Delete all charges',
                message: 'This removes all outstanding room charges shown here without recording payment.',
                detail: `${bookerName} · ${formatMoney(total)}`,
                confirmLabel: 'Delete all',
                isDanger: true,
                action: 'deleteAll',
              })}
            >
              Delete all
            </S.DeleteAllBtn>
          ) : null}
        </S.ActionsRow>
      </S.ChargesColumn>
    </S.Container>
  );
};

export default RoomChargesPanel;
